import React, { useState } from "react";
import PropTypes from "prop-types";

/**
 * Transcript-facing confirm card for ONE captured food-preference item
 * (kind + ingredient). Food preferences is a whole-doc PUT resource
 * with no per-item Add endpoint in the standalone rail — the AI
 * captures per-ingredient, but each Save & next just merges the
 * ingredient into the appropriate FoodBehavior chip list on the
 * transcript service side (read-modify-write per item).
 *
 * The form is deliberately minimal — the reviewer's only decisions
 * are "confirm this classification" or "reclassify to a different
 * list". No autocomplete: the AI's ingredient string carries through
 * to the server, which drops it in the chip list as-is
 * (source="free-text" per the transcript-side adapter, since the AI
 * has no catalog binding today; the standalone reviewer can pick a
 * matching catalog id later via the inline editor).
 */

const KIND_OPTIONS = [
  { key: "likes",        label: "Like" },
  { key: "dislikes",     label: "Dislike" },
  { key: "restrictions", label: "Restriction" },
  { key: "limitations",  label: "Limitation" },
];

const normaliseKind = (raw) => {
  const t = String(raw || "").toLowerCase();
  const aliases = {
    like: "likes", likes: "likes",
    dislike: "dislikes", dislikes: "dislikes",
    restriction: "restrictions", restrictions: "restrictions",
    avoid: "restrictions", avoidance: "restrictions",
    limitation: "limitations", limitations: "limitations",
    limit: "limitations",
  };
  return aliases[t] || "likes";
};

const FoodPreferenceForm = ({
  formId,
  initialPayload,
  saving,
  error,
  onSave,
  onClose,
  themeClass,
  lockName,
}) => {
  const p = initialPayload || {};

  const [kind, setKind] = useState(() => normaliseKind(p.kind));
  const [ingredient, setIngredient] = useState(() =>
    (p.ingredient || p.name || p.value || "").toString()
  );

  const trimmed = ingredient.trim();
  const busy = !!saving;

  const canSubmit = !busy && !!trimmed && !!kind;

  const submit = () => {
    if (!canSubmit) return;
    if (typeof onSave === "function") {
      onSave({ kind, ingredient: trimmed });
    }
  };
  const handleClose = () => onClose(formId);

  const rootClass = ["cc3-form", "fp3-form", themeClass || ""].filter(Boolean).join(" ");
  const displayError = error || "";

  return (
    <div className={rootClass}>
      <div className="cc3-lbl-row">
        <label className="cc3-lbl">
          Food preference
          {lockName && trimmed && (
            <span className="cc3-update-name">&middot; {trimmed}</span>
          )}
        </label>
      </div>

      <div className="cc3-fold-inner">
        <div className="cc3-field">
          <label className="cc3-lbl" htmlFor={`fpf-ing-${formId}`}>
            Ingredient<span className="cc3-req">*</span>
          </label>
          <input
            id={`fpf-ing-${formId}`}
            className="sec-input"
            type="text"
            value={ingredient}
            onChange={(e) => setIngredient(e.target.value)}
            placeholder="e.g. mango"
            disabled={busy || lockName}
            maxLength={80}
            aria-label="Ingredient"
          />
        </div>

        <div className="cc3-field">
          <label className="cc3-lbl">
            Classify as<span className="cc3-req">*</span>
          </label>
          <div className="yn-toggle" role="radiogroup" aria-label="Classify as">
            {KIND_OPTIONS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={kind === key}
                className={`yn-opt${kind === key ? " is-on" : ""}`}
                onClick={() => setKind(key)}
                disabled={busy}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {displayError && (
          <div className="cc-feedback is-err">{displayError}</div>
        )}

        <div className="cc3-foot">
          <button type="button" className="cc3-cancel" onClick={handleClose}>
            {busy ? "Close" : "Cancel"}
          </button>
          <button
            type="button"
            className={`cc3-save${themeClass ? ` ${themeClass}` : ""}`}
            onClick={submit}
            disabled={!canSubmit}
          >
            {busy ? "Adding…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
};

FoodPreferenceForm.propTypes = {
  formId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  initialPayload: PropTypes.object,
  saving: PropTypes.bool,
  error: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  themeClass: PropTypes.string,
  /** Freeze the ingredient field (transcript click-to-edit re-open). */
  lockName: PropTypes.bool,
};

FoodPreferenceForm.defaultProps = {
  initialPayload: null,
  saving: false,
  error: "",
  themeClass: "",
  lockName: false,
};

export default FoodPreferenceForm;
